import { LabStageClock } from './labSdk.types'

/** One stage of the Lab's plan for a job */
export interface LabOrderStage {
  name: string
  /** Overall percentage at which the stage starts and ends */
  pct: [number, number]
  /** Typical duration (the Lab's estimate — per speed tier for a cloud generation) */
  estMs: number
}

const isPercent = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

/**
 * The job's stage plan as the platform relays it — `stages` of a progress frame or of `GET /job/{id}`'s `progress`
 * (`AiJobStageDTO`). Anything malformed is dropped rather than trusted — without stages the overlay simply follows
 * the real events.
 */
export const parseLabOrderStages = (stages: unknown): LabOrderStage[] => {
  if (!Array.isArray(stages)) return []
  return stages.flatMap((stage): LabOrderStage[] => {
    const { name, startPercent, endPercent, estMs } = (stage ?? {}) as Record<string, unknown>
    const isValid = typeof name === 'string'
      && isPercent(startPercent) && isPercent(endPercent) && endPercent > startPercent
      && isPercent(estMs) && estMs > 0
    return isValid ? [{ name, pct: [startPercent, endPercent], estMs }] : []
  })
}

/**
 * The clock after a progress report: a new one when the report announces a stage of the order, the running one
 * otherwise — sub-labels (`sampling 5/8`, `detect: human face`) belong to the running stage — with its base raised
 * to any real progress, kept under the stage's ceiling.
 */
export const nextLabStageClock = (
  stages: LabOrderStage[],
  previous: LabStageClock | undefined,
  progress: { stage: string, percent: number },
  now: number
): LabStageClock | undefined => {
  const stage = stages.find((item) => item.name === progress.stage)
  if (stage && stage.name !== previous?.name) {
    const [start, ceil] = stage.pct
    return { name: stage.name, start, ceil, base: Math.min(ceil - 1, Math.max(start, progress.percent)), estMs: stage.estMs, startedAt: now }
  }
  if (!previous) return undefined
  const base = Math.max(previous.base, Math.min(previous.ceil - 1, progress.percent))
  return base === previous.base ? previous : { ...previous, base }
}

/**
 * Where the running stage should be by now — the Lab Playground's curve: eases from the base towards the ceiling,
 * fast first then ever slower (time constant 70% of the typical duration), and holds one point under the ceiling, so
 * a generation that runs long never reads as finished. Only a real event moves past it.
 */
export const simulateLabStagePercent = (clock: LabStageClock, now: number): number => {
  const timeConstant = Math.max(1000, clock.estMs * 0.7)
  const eased = 1 - Math.exp(-Math.max(0, now - clock.startedAt) / timeConstant)
  return Math.max(clock.base, Math.min(clock.ceil - 1, clock.base + (clock.ceil - clock.base) * eased))
}

/** Share of the stage done at this overall percentage, 0..1 — what its segment of the bar shows. */
export const labStageFraction = (clock: LabStageClock, percent: number): number =>
  Math.min(1, Math.max(0, (percent - clock.start) / (clock.ceil - clock.start)))

/** Time left on the stage's estimate. */
export const labStageRemainingMs = (clock: LabStageClock, now: number): number =>
  Math.max(0, clock.estMs - Math.max(0, now - clock.startedAt))
